import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import { adminService } from "../../../../services/admin";
import type {
  Routine,
  Exercise,
  SetType,
  UserProfile,
  RoutineExercise,
  RoutineSet,
} from "../../../../types/db";
import {
  Plus,
  Save,
  Trash2,
  ArrowLeft,
  Search,
  X,
  ChevronDown,
  ChevronUp,
  Copy,
  Dumbbell,
} from "lucide-react";

export const AdminTrainingRoutines = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const navigate = useNavigate();

  const [user, setUser] = useState<UserProfile | null>(null);
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [setTypes, setSetTypes] = useState<SetType[]>([]);
  const [loading, setLoading] = useState(true);
  // Editor State
  const [isEditing, setIsEditing] = useState(false);
  const [currentRoutine, setCurrentRoutine] = useState<Partial<Routine>>({
    routine_name: "",
    day: 1,
    active: true,
    exercises: [],
  });

  // Exercise Selection Modal
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [exerciseSearch, setExerciseSearch] = useState("");

  useEffect(() => {
    if (!userId) {
      navigate("/admin/users");
      return;
    }

    const loadData = async () => {
      setLoading(true);
      const [u, r, ex, st] = await Promise.all([
        adminService.getUser(userId),
        adminService.getUserRoutines(userId),
        adminService.getExercises(),
        adminService.getSetTypes(),
      ]);
      setUser(u);
      setRoutines(r);
      setExercises(ex);
      setSetTypes(st);
      setLoading(false);
    };

    loadData();
  }, [userId, navigate]);

  const handleCreateRoutine = () => {
    setCurrentRoutine({
      routine_name: "New Routine",
      day: 1,
      user_id: userId!,
      active: true,
      exercises: [],
    });
    setIsEditing(true);
  };

  const handleEditRoutine = (routine: Routine) => {
    setCurrentRoutine(JSON.parse(JSON.stringify(routine))); // Deep copy
    setIsEditing(true);
  };

  const handleSaveRoutine = async () => {
    if (!currentRoutine.routine_name || !currentRoutine.user_id) return;

    // Validate
    if (!currentRoutine.exercises || currentRoutine.exercises.length === 0) {
      alert("Please add at least one exercise.");
      return;
    }

    setLoading(true);
    let result;
    if (currentRoutine.$id) {
      result = await adminService.updateRoutine(
        currentRoutine.$id,
        currentRoutine,
      );
    } else {
      result = await adminService.createRoutine(currentRoutine as Routine);
    }
    setLoading(false);

    if (result) {
      setIsEditing(false);
      // Refresh routines
      const r = await adminService.getUserRoutines(userId!);
      setRoutines(r);
    } else {
      alert("Failed to save routine");
    }
  };

  const handleDeleteRoutine = async (id?: string) => {
    if (!id) {
      alert("Invalid routine ID");
      return;
    }
    if (!confirm("Are you sure?")) return;
    await adminService.deleteRoutine(id);
    const r = await adminService.getUserRoutines(userId!);
    setRoutines(r);
  };

  // --- Editor Logic ---

  const addExerciseToRoutine = (exercise: Exercise) => {
    let uuid = crypto.randomUUID();
    let id = `rtn_exr_${uuid}`;
    console.log("[addExerciseToRoutine]", id);

    const newRoutineExercise: RoutineExercise = {
      id: id,
      exerciseId: exercise.exerciseId,
      order: (currentRoutine.exercises?.length || 0) + 1,
      sets: [
        {
          typeId: setTypes[0]?.id || "exr_type_01",
          reps: "10",
          weight: "0",
          restSeconds: 60,
        },
      ],
    };

    setCurrentRoutine((prev) => ({
      ...prev,
      exercises: [...(prev.exercises || []), newRoutineExercise],
    }));
    setShowExerciseModal(false);
    setExerciseSearch("");
  };

  const removeExercise = (rtnExId: string) => {
    setCurrentRoutine((prev) => ({
      ...prev,
      exercises: prev.exercises?.filter((e) => e.id !== rtnExId),
    }));
  };

  const updateSet = (
    rtnExId: string,
    setIndex: number,
    field: keyof RoutineSet,
    value: any,
  ) => {
    setCurrentRoutine((prev) => ({
      ...prev,
      exercises: prev.exercises?.map((ex) => {
        if (ex.id !== rtnExId) return ex;
        const newSets = [...ex.sets];
        newSets[setIndex] = { ...newSets[setIndex], [field]: value };
        return { ...ex, sets: newSets };
      }),
    }));
  };

  const addSet = (rtnExId: string) => {
    setCurrentRoutine((prev) => ({
      ...prev,
      exercises: prev.exercises?.map((ex) => {
        if (ex.id !== rtnExId) return ex;
        const lastSet = ex.sets[ex.sets.length - 1];
        return { ...ex, sets: [...ex.sets, { ...lastSet }] };
      }),
    }));
  };

  const removeSet = (rtnExId: string, setIndex: number) => {
    setCurrentRoutine((prev) => ({
      ...prev,
      exercises: prev.exercises?.map((ex) => {
        if (ex.id !== rtnExId) return ex;
        return { ...ex, sets: ex.sets.filter((_, i) => i !== setIndex) };
      }),
    }));
  };

  const filteredExercises = useMemo(() => {
    if (!exerciseSearch) return [];
    const lower = exerciseSearch.toLowerCase();
    return exercises
      .filter(
        (e) =>
          e.name.toLowerCase().includes(lower) ||
          e.targetMuscles?.some((m) => m.toLowerCase().includes(lower)) ||
          e.bodyParts?.some((b) => b.toLowerCase().includes(lower)) ||
          e.equipments?.some((eq) => eq.toLowerCase().includes(lower)),
      )
      .slice(0, 50); // Limit results
  }, [exercises, exerciseSearch]);

  const days = [
    "Domenica",
    "Lunedì",
    "Martedì",
    "Mercoledì",
    "Giovedì",
    "Venerdì",
    "Sabato",
  ];

  if (loading && !user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <AdminLayout>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() =>
            isEditing ? setIsEditing(false) : navigate("/admin/users")
          }
          className="p-2 rounded-full hover:bg-muted/50"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">
            {isEditing
              ? currentRoutine.$id
                ? "Edit Routine"
                : "Create Routine"
              : `Routines for ${user?.name}`}
          </h1>
          <p className="text-muted-foreground text-sm">
            {isEditing
              ? "Configure exercises, sets and reps"
              : "Manage training program"}
          </p>
        </div>
      </div>

      {!isEditing ? (
        <div className="grid gap-6">
          <div className="flex justify-end">
            <button
              onClick={handleCreateRoutine}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Routine
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {routines.map((routine) => (
              <div
                key={routine.$id}
                className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-6 relative group"
              >
                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleEditRoutine(routine)}
                    className="p-2 bg-muted rounded-full hover:bg-muted/80"
                  >
                    <Dumbbell className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteRoutine(routine.$id)}
                    className="p-2 bg-destructive/10 text-destructive rounded-full hover:bg-destructive/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <h3 className="text-lg font-bold mb-1">
                  {routine.routine_name}
                </h3>
                <div className="inline-block px-2 py-1 bg-primary/10 text-primary text-xs rounded-full mb-4">
                  {days[routine.day]}
                </div>
                <div className="space-y-2">
                  {routine.exercises.slice(0, 3).map((ex, i) => {
                    const originalEx = exercises.find(
                      (e) => e.exerciseId === ex.exerciseId,
                    );
                    return (
                      <div
                        key={i}
                        className="flex items-center gap-2 text-sm text-muted-foreground"
                      >
                        <div className="w-1 h-1 rounded-full bg-border" />
                        <span className="truncate">
                          {originalEx?.name || "Unknown exercise"}
                        </span>
                        <span className="text-xs opacity-50">
                          ({ex.sets.length} sets)
                        </span>
                      </div>
                    );
                  })}
                  {routine.exercises.length > 3 && (
                    <div className="text-xs text-muted-foreground pt-1">
                      + {routine.exercises.length - 3} more exercises
                    </div>
                  )}
                </div>
              </div>
            ))}
            {routines.length === 0 && (
              <div className="col-span-full py-12 text-center text-muted-foreground border-2 border-dashed border-border rounded-xl">
                No routines found. Create one to get started.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6 max-w-5xl mx-auto">
          {/* Meta Data */}
          <div className="p-6 rounded-xl border border-border bg-card/50 backdrop-blur-sm space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Routine Name
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:ring-2 focus:ring-primary/20 outline-none"
                  value={currentRoutine.routine_name}
                  onChange={(e) =>
                    setCurrentRoutine((prev) => ({
                      ...prev,
                      routine_name: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Day of Week
                </label>
                <select
                  className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:ring-2 focus:ring-primary/20 outline-none"
                  value={currentRoutine.day}
                  onChange={(e) =>
                    setCurrentRoutine((prev) => ({
                      ...prev,
                      day: parseInt(e.target.value),
                    }))
                  }
                >
                  {days.map((d, i) => (
                    <option key={i} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Exercises List */}
          <div className="space-y-4">
            {currentRoutine.exercises?.map((routineEx, index) => {
              const originalExercise = exercises.find(
                (e) => e.exerciseId === routineEx.exerciseId,
              );
              return (
                <div
                  key={routineEx.id}
                  className="p-6 rounded-xl border border-border bg-card/50 backdrop-blur-sm"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex gap-4">
                      <img
                        src={
                          originalExercise?.gifUrl ||
                          originalExercise?.imageUrl ||
                          "/placeholder.png"
                        }
                        alt=""
                        className="w-16 h-16 rounded-lg object-cover bg-muted"
                      />
                      <div>
                        <h3 className="font-bold">
                          {originalExercise?.name_it ||
                            originalExercise?.name ||
                            "Unknown Exercise"}
                        </h3>
                        <div className="flex gap-2 mt-1">
                          {originalExercise?.bodyParts.map((bp) => (
                            <span
                              key={bp}
                              className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                            >
                              {bp}
                            </span>
                          ))}
                          {originalExercise?.gifUrl && (
                            <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-green-500/40 text-primary flex items-center gap-1">
                              GIF
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeExercise(routineEx.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Sets Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-muted-foreground text-xs uppercase tracking-wider">
                          <th className="pb-2 w-8">Set</th>
                          <th className="pb-2 min-w-[140px]">Type</th>
                          <th className="pb-2 w-24">Reps</th>
                          <th className="pb-2 w-24">Weight (kg)</th>
                          <th className="pb-2 w-24">Rest (s)</th>
                          <th className="pb-2 w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {routineEx.sets.map((set, setIdx) => (
                          <tr key={setIdx}>
                            <td className="py-2 font-medium bg-muted/20 text-center rounded-l-md">
                              {setIdx + 1}
                            </td>
                            <td className="py-2 px-2 bg-muted/20">
                              <select
                                className="w-full bg-transparent border-0 p-0 text-sm focus:ring-0"
                                value={set.typeId}
                                onChange={(e) =>
                                  updateSet(
                                    routineEx.id,
                                    setIdx,
                                    "typeId",
                                    e.target.value,
                                  )
                                }
                              >
                                {setTypes.map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="py-2 bg-muted/20">
                              <input
                                type="text"
                                className="w-full bg-transparent border-0 p-0 text-center text-sm focus:ring-0"
                                value={set.reps}
                                onChange={(e) =>
                                  updateSet(
                                    routineEx.id,
                                    setIdx,
                                    "reps",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td className="py-2 bg-muted/20">
                              <input
                                type="text"
                                className="w-full bg-transparent border-0 p-0 text-center text-sm focus:ring-0"
                                value={set.weight}
                                onChange={(e) =>
                                  updateSet(
                                    routineEx.id,
                                    setIdx,
                                    "weight",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td className="py-2 bg-muted/20">
                              <input
                                type="number"
                                className="w-full bg-transparent border-0 p-0 text-center text-sm focus:ring-0"
                                value={set.restSeconds}
                                onChange={(e) =>
                                  updateSet(
                                    routineEx.id,
                                    setIdx,
                                    "restSeconds",
                                    parseInt(e.target.value),
                                  )
                                }
                              />
                            </td>
                            <td className="py-2 px-2 text-center bg-muted/20 rounded-r-md">
                              <button
                                onClick={() => removeSet(routineEx.id, setIdx)}
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-2 text-center">
                    <button
                      onClick={() => addSet(routineEx.id)}
                      className="text-xs text-primary hover:underline font-medium"
                    >
                      + Add Set
                    </button>
                  </div>
                </div>
              );
            })}

            <button
              onClick={() => setShowExerciseModal(true)}
              className="w-full py-8 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center text-muted-foreground hover:border-primary/50 hover:text-primary transition-all hover:bg-muted/10"
            >
              <Plus className="w-8 h-8 mb-2" />
              Add Exercise
            </button>
          </div>

          <div className="flex items-center justify-end gap-4 border-t border-border pt-6">
            <button
              onClick={() => setIsEditing(false)}
              className="px-6 py-2 rounded-lg hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveRoutine}
              disabled={loading}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {loading ? "Saving..." : "Save Routine"}
            </button>
          </div>
        </div>
      )}

      {/* Exercise Selection Modal */}
      {showExerciseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-background w-full max-w-2xl max-h-[80vh] rounded-2xl flex flex-col shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-bold">Select Exercise</h2>
              <button onClick={() => setShowExerciseModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search exercises..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-muted/50 border border-border focus:ring-2 focus:ring-primary/20 outline-none"
                  value={exerciseSearch}
                  onChange={(e) => setExerciseSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {filteredExercises.map((ex) => (
                <button
                  key={ex.exerciseId}
                  onClick={() => addExerciseToRoutine(ex)}
                  className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 text-left transition-colors border border-transparent hover:border-primary/10"
                >
                  <img
                    src={ex.gifUrl || ex.imageUrl || "/placeholder.png"}
                    alt=""
                    className="w-12 h-12 rounded object-cover bg-muted"
                  />
                  <div>
                    <div className="font-medium">{ex.name_it || ex.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {ex.bodyParts.join(", ")}
                    </div>
                  </div>
                  {ex.gifUrl && (
                    <span className="ml-auto text-xs text-primary flex items-center gap-1">
                      GIF
                    </span>
                  )}
                </button>
              ))}
              {filteredExercises.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  {exerciseSearch
                    ? "No exercises found."
                    : "Start typing to search..."}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
